import React from 'react';
import { Bell, CheckCircle, X, CheckCheck } from 'lucide-react';
import ClientPageLayout from '../../components/shared/layouts/ClientPageLayout';
import { ClientEmptyState } from '../../components/client/guidance';
import { NotificationSkeleton } from '../../components/shared/ClientPageSkeleton';
import { useNotifications, useMarkNotificationAsRead, useMarkAllNotificationsAsRead, useDeleteNotification } from '../../services/notificationService';

const NOTIFICATIONS_SUBTITLE = 'Booking updates, invoices, and platform alerts in one place.';

const ClientNotifications = () => {
  const { data: notificationsData, isLoading } = useNotifications({ per_page: 50 });
  const markAsRead = useMarkNotificationAsRead();
  const markAllAsRead = useMarkAllNotificationsAsRead();
  const deleteNotification = useDeleteNotification();

  const notifications = notificationsData?.data?.data || [];
  const unreadCount = notifications.filter(n => !n.is_read).length;

  const handleMarkAsRead = async (id) => {
    await markAsRead.mutateAsync(id);
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead.mutateAsync();
  };

  const handleDelete = async (id) => {
    await deleteNotification.mutateAsync(id);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'booking_confirmed':
      case 'booking_completed':
        return { icon: '✅', color: 'text-emerald-600 dark:text-emerald-400' };
      case 'booking_cancelled':
        return { icon: '❌', color: 'text-red-600 dark:text-red-400' };
      case 'booking_in_progress':
        return { icon: '▶️', color: 'text-sky-600 dark:text-sky-400' };
      case 'payment_received':
        return { icon: '💰', color: 'text-emerald-600 dark:text-emerald-400' };
      case 'event_started':
        return { icon: '🎬', color: 'text-sky-600 dark:text-sky-400' };
      case 'event_ended':
        return { icon: '🏁', color: 'text-slate-600 dark:text-slate-400' };
      case 'broadcast':
        return { icon: '📢', color: 'text-sky-600 dark:text-sky-400' };
      case 'review_received':
        return { icon: '⭐', color: 'text-amber-600 dark:text-amber-400' };
      case 'message_received':
        return { icon: '💬', color: 'text-sky-600 dark:text-sky-400' };
      case 'reminder':
        return { icon: '⏰', color: 'text-amber-600 dark:text-amber-400' };
      default:
        return { icon: '🔔', color: 'text-gray-600 dark:text-gray-400' };
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minute${Math.floor(seconds / 60) > 1 ? 's' : ''} ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hour${Math.floor(seconds / 3600) > 1 ? 's' : ''} ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)} day${Math.floor(seconds / 86400) > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const layout = {
    title: 'Notifications',
    subtitle: NOTIFICATIONS_SUBTITLE,
    guideKey: 'notifications',
  };

  if (isLoading) {
    return (
      <ClientPageLayout {...layout}>
        <div className="space-y-5">
          <div className="rounded-2xl border border-gray-200/80 bg-white p-4 sm:p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <div className="flex items-center justify-between mb-4">
              <div className="h-5 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            </div>
            <NotificationSkeleton count={5} />
          </div>
        </div>
      </ClientPageLayout>
    );
  }

  return (
    <ClientPageLayout {...layout}>
      <div className="space-y-5">
        <div className="rounded-2xl border border-gray-200/80 bg-white p-4 sm:p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-50">Inbox</h2>
              {unreadCount > 0 ? (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-sky-50 text-sky-800 border border-sky-200/80 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-800/50 mr-1.5">
                    {unreadCount} unread
                  </span>
                  notification{unreadCount !== 1 ? 's' : ''}
                </p>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">You’re all caught up</p>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={handleMarkAllAsRead}
                disabled={markAllAsRead.isPending}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-sky-700 dark:text-sky-400 hover:text-sky-900 dark:hover:text-sky-300 disabled:opacity-50"
              >
                <CheckCheck className="w-4 h-4" />
                <span>Mark all as read</span>
              </button>
            )}
          </div>
        </div>

        {notifications.length === 0 ? (
          <ClientEmptyState
            icon={Bell}
            title="No notifications"
            description="Booking updates, invoices, and platform alerts will show up here."
            tips={['You’ll see confirmations after you book', 'Adjust preferences anytime in Settings']}
          />
        ) : (
          <div className="space-y-3">
            {notifications.map((notification) => {
              const { icon, color } = getNotificationIcon(notification.type);
              return (
                <article
                  key={notification.id}
                  className={`rounded-2xl border p-4 sm:p-5 shadow-sm transition-colors ${
                    notification.is_read
                      ? 'border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900'
                      : 'border-sky-200/80 bg-sky-50/80 dark:border-sky-800/50 dark:bg-sky-950/30'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`flex-shrink-0 w-10 h-10 rounded-xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-900 flex items-center justify-center text-lg ${color}`}>
                      {icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-50">
                              {notification.title}
                            </h3>
                            {notification.is_broadcast && (
                              <span className="text-xs bg-sky-100 text-sky-800 border border-sky-200/80 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-800/50 px-2 py-0.5 rounded-full font-medium">
                                Broadcast
                              </span>
                            )}
                            {!notification.is_read && (
                              <span className="w-2 h-2 rounded-full bg-sky-500 flex-shrink-0" aria-hidden />
                            )}
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                            {formatTimeAgo(notification.created_at)}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {!notification.is_read && (
                            <button
                              type="button"
                              onClick={() => handleMarkAsRead(notification.id)}
                              disabled={markAsRead.isPending}
                              className="p-2 text-sky-600 dark:text-sky-400 hover:bg-sky-100 dark:hover:bg-sky-950/50 !rounded-xl disabled:opacity-50"
                              title="Mark as read"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleDelete(notification.id)}
                            disabled={deleteNotification.isPending}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 !rounded-xl disabled:opacity-50"
                            title="Delete notification"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </ClientPageLayout>
  );
};

export default ClientNotifications;
